import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { COMMENTS_CONSTANTS } from '@/domain/constants/commentsConstants';

/**
 * Math Utilities
 *
 * Static utility methods for mathematical operations and formatting.
 * Pure functions with no state - can be used anywhere.
 */
export class MathUtils {
  /**
   * Remove trailing zeros after decimal point
   * Matches Flutter's MathUtils.removeTrailingZero
   * 
   * @param number - The number to format
   * @returns Formatted string with trailing zeros removed
   * 
   * @example
   * MathUtils.removeTrailingZero(175.00) // "175"
   * MathUtils.removeTrailingZero(175.50) // "175.5"
   * MathUtils.removeTrailingZero(175) // "175"
   */
  static removeTrailingZero(number: number): string {
    if (Number.isInteger(number)) return number.toString();
    return parseFloat(number.toFixed(2)).toString();
  }

  /**
   * Format count with K/M suffixes (e.g., 1.2K, 1.5M)
   * Handles undefined and zero values gracefully
   * 
   * @param count - The number to format (can be undefined)
   * @returns Formatted string representation
   * 
   * @example
   * MathUtils.formatCount(0) // "0"
   * MathUtils.formatCount(500) // "500"
   * MathUtils.formatCount(1500) // "1.5K"
   * MathUtils.formatCount(2500000) // "2.5M"
   * MathUtils.formatCount(undefined) // "0"
   */
  static formatCount(count: number | undefined): string {
    if (!count || count === 0) return '0';
    if (count < 1000) return count.toString();
    if (count < 1000000) return `${(count / 1000).toFixed(1)}K`;
    return `${(count / 1000000).toFixed(1)}M`;
  }

  /**
   * Clamp a number between min and max values
   * 
   * @param value - The value to clamp
   * @param min - Minimum allowed value
   * @param max - Maximum allowed value
   * @returns Clamped value
   */
  static clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  /**
   * Linear interpolation between two values
   * 
   * @param start - Starting value
   * @param end - Ending value
   * @param t - Interpolation factor (0-1)
   * @returns Interpolated value
   */
  static lerp(start: number, end: number, t: number): number {
    return start + (end - start) * t;
  }

  /**
   * Round number to specified decimal places
   * 
   * @param value - Number to round
   * @param decimals - Number of decimal places (default: 2)
   * @returns Rounded number
   */
  static roundTo(value: number, decimals: number = 2): number {
    const multiplier = Math.pow(10, decimals);
    return Math.round(value * multiplier) / multiplier;
  }

  /**
   * Calculate percentage
   *
   * @param value - The value
   * @param total - The total value
   * @returns Percentage (0-100)
   */
  static percentage(value: number, total: number): number {
    if (total === 0) return 0;
    return (value / total) * 100;
  }

  /**
   * Format the integer part of a number with locale-based thousand separators
   *
   * @param num - The number to format
   * @returns Locale-formatted integer part (e.g., 1234.56 → "1,234")
   */
  static formatIntegerPart(num: number): string {
    return Math.floor(num).toLocaleString();
  }

  /**
   * Format a number to exactly two decimal places
   * Matches Flutter's MathUtils.roundTwoDecimals
   *
   * @param num - The number to format
   * @returns String with exactly 2 decimal places (e.g., 175.5 → "175.50")
   */
  static roundTwoDecimals(num: number): string {
    return num.toFixed(2);
  }

  /**
   * Convert seconds to minutes, rounded up
   */
  static secondsToMinutesCeil(seconds: number): number {
    return Math.ceil(seconds / 60);
  }

  /**
   * Round a coin amount UP for display.
   *
   * Ported from Flutter, which has no `MathUtils` member for this and inlines
   * the rule at its single render site: the gift cell renders
   * `"${gift.coins.ceil()}  "` (`lib/presentation/bottom_sheet/send_gift_bottom_sheet.dart:603`)
   * over a `late double coins` (`lib/data/models/gift.dart:24`), so a fractional
   * price reads `11` on Flutter and must read `11` on web too. Web gives the
   * rule a named owner here so the next coins surface that needs it reuses the
   * symbol instead of re-inlining `Math.ceil`.
   *
   * DISPLAY ONLY — every arithmetic path keeps the raw double, exactly as
   * Flutter does: the recharge check
   * (`send_gift_bottom_sheet.dart:108-109`, `selectedGift.coins * instances`)
   * and the persisted `GiftInfo.coins` (`:193`) both multiply the unrounded
   * value, which `useSendGift` already matches. Never route a total, a balance
   * comparison, or a written field through this helper.
   *
   * @param coins - The raw coin amount (may be fractional)
   * @returns The amount rounded up to an integer, for rendering only
   *
   * @example
   * MathUtils.ceilCoinsForDisplay(10) // 10
   * MathUtils.ceilCoinsForDisplay(10.01) // 11
   */
  static ceilCoinsForDisplay(coins: number): number {
    return Math.ceil(coins);
  }

  /**
   * Get the decimal portion of a number as a display string (e.g., for currency display)
   *
   * @param num - The number to extract fractional part from
   * @returns Decimal display string (e.g., 1.50 → ".50", 1 → ".00")
   */
  static getDecimalDisplay(num: number): string {
    const frac = Math.abs(num) % 1;
    if (frac === 0) return '.00';
    return '.' + frac.toFixed(2).split('.')[1];
  }

  /**
   * Convert a USD amount to its coins equivalent.
   * Matches Flutter's MathUtils.coinsFromUSD.
   */
  static coinsFromUSD(dollars: number): number {
    return dollars * remoteConfigService.dollarToCoinsRatio;
  }

  /**
   * Convert a diamonds amount to its USD equivalent.
   * Matches Flutter's MathUtils.diamondsToUSD.
   */
  static diamondsToUSD(diamonds: number): number {
    return diamonds * remoteConfigService.diamondsToDollarsRatio;
  }

  /**
   * Convert a coins amount to its USD equivalent.
   * Matches Flutter's MathUtils.coinsToUSD.
   */
  static coinsToUSD(coins: number): number {
    return coins / remoteConfigService.dollarToCoinsRatio;
  }

  /**
   * Format a USD amount for display in the unlock-price text field.
   * Matches Flutter's MathUtils.getWalletAmount: always 2 decimals.
   */
  static getWalletAmount(dollars: number): string {
    return dollars.toFixed(2);
  }

  /**
   * Round a number to two decimals, dropping the decimals entirely when the
   * value is a whole number.
   * Matches Flutter's MathUtils.roundDoubleTwoDecimals
   * (`lib/presentation/utils/math_utils.dart:90-95`):
   * `number.toStringAsFixed(number.truncateToDouble() == number ? 0 : 2)`.
   *
   * @param number - The number to format (null → "0")
   * @returns Formatted string (e.g. 175 → "175", 175.5 → "175.50")
   */
  static roundDoubleTwoDecimals(number: number | null | undefined): string {
    if (number == null) {
      return '0';
    }
    return Number.isInteger(number) ? number.toString() : number.toFixed(2);
  }

  /**
   * Compact, locale-aware reaction count (e.g. 1200 → "1.2K", 1_500_000 → "1.5M").
   * Matches Flutter's MathUtils.getReactionsIntlNumber
   * (`lib/presentation/utils/math_utils.dart:140-146`), which uses
   * `NumberFormat.compact()`. The web equivalent is `Intl.NumberFormat` with
   * `notation: 'compact'`.
   *
   * @param number - The number to format (null → "0")
   * @returns Compact-notation string
   */
  static getReactionsIntlNumber(number: number | null | undefined): string {
    if (number == null) {
      return '0';
    }
    return new Intl.NumberFormat(undefined, { notation: 'compact' }).format(number);
  }

  /**
   * Client-side popularity used to seed a newly-added root roadmap comment's
   * `popularity`, ordering new comments correctly until the server recomputes.
   * Matches Flutter's MathUtils.getUserPopularityForComment
   * (`lib/presentation/utils/math_utils.dart:108-120`): returns
   * `defaultUserPopularityForComment` when `popularity == null || popularity == 0`,
   * else `min(popularity, maxUserPopularityForComment)`.
   *
   * @param popularity - The user's popularity (null/undefined/0 → default)
   * @returns Seed popularity for the comment
   */
  static getUserPopularityForComment(popularity: number | null | undefined): number {
    if (popularity == null || popularity === 0) {
      return COMMENTS_CONSTANTS.DEFAULT_USER_POPULARITY_FOR_COMMENT;
    }
    return Math.min(popularity, COMMENTS_CONSTANTS.MAX_USER_POPULARITY_FOR_COMMENT);
  }
}
